import React, { useRef, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';

const AddressAutocompleteInput = ({ 
  onAddressSelect, 
  placeholder = "Start typing your address...", 
  disabled = false,
  className = ""
}) => {
  const containerRef = useRef(null);
  const placesLibrary = useMapsLibrary('places');

  useEffect(() => {
    if (!placesLibrary || !containerRef.current) return;

    // console.log('Creating PlaceAutocompleteElement with beta API...');

    // Create the PlaceAutocompleteElement (requires beta API)
    const autocomplete = new placesLibrary.PlaceAutocompleteElement({
      componentRestrictions: { country: ['us'] },
      types: ['address']
    });

    // Set properties
    autocomplete.placeholder = placeholder;
    autocomplete.disabled = disabled;

    // console.log('PlaceAutocompleteElement created:', autocomplete);

    // Listen for the correct event (gmp-select in beta)
    const handlePlaceSelect = async (event) => {
      // console.log('=== gmp-select event fired ===');
      // console.log('Event:', event);
      
      try {
        // Get the place from the prediction
        const place = event.placePrediction.toPlace();
        // console.log('Place from prediction:', place);
        
        // Fetch the fields we need (use object format for beta API)
        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress']
        });
        // console.log('Place after fetchFields:', place);
        
        if (!place.addressComponents) {
          console.error('No address components found');
          return;
        }

        // console.log('Address components:', place.addressComponents);

        // Parse address components
        const addressData = {
          address_line_1: '',
          address_line_2: '',
          city: '',
          state: '',
          zip_code: '',
          country: 'United States'
        };

        place.addressComponents.forEach((component, index) => {
          const types = component.types;
          // console.log(`Component ${index}:`, {
          //   types: types,
          //   longText: component.longText,
          //   shortText: component.shortText
          // });
          
          if (types.includes('street_number')) {
            addressData.address_line_1 = component.longText + ' ';
            // console.log('Found street_number:', component.longText);
          }
          
          if (types.includes('route')) {
            addressData.address_line_1 += component.longText;
            // console.log('Found route:', component.longText);
          }
          
          if (types.includes('locality')) {
            addressData.city = component.longText;
            // console.log('Found locality:', component.longText);
          }
          
          if (types.includes('administrative_area_level_1')) {
            addressData.state = component.shortText;
            // console.log('Found administrative_area_level_1:', component.shortText);
          }
          
          if (types.includes('postal_code')) {
            addressData.zip_code = component.longText;
            // console.log('Found postal_code:', component.longText);
          }
          
          if (types.includes('country')) {
            addressData.country = component.longText;
            // console.log('Found country:', component.longText);
          }
        });

        addressData.address_line_1 = addressData.address_line_1.trim();

        // console.log('Final address data:', addressData);
        onAddressSelect(addressData);
      } catch (error) {
        console.error('Error handling place select:', error);
      }
    };

    // Add the event listener for the new API
    autocomplete.addEventListener('gmp-select', handlePlaceSelect);

    // Append to container
    containerRef.current.appendChild(autocomplete);

    // Cleanup
    return () => {
      autocomplete.removeEventListener('gmp-select', handlePlaceSelect);
      if (autocomplete && containerRef.current?.contains(autocomplete)) {
        containerRef.current.removeChild(autocomplete);
      }
    };
  }, [placesLibrary, onAddressSelect, placeholder, disabled]);

  return (
    <div className={className}>
      <div 
        ref={containerRef} 
        className="address-autocomplete-container"
        style={{ 
          width: '100%',
          '--gmp-option-selected-background': '#e3f2fd',
          '--gmp-text-font-family': 'inherit'
        }}
      />
      <Form.Text className="text-muted">
        Start typing your address to see suggestions
      </Form.Text>
    </div>
  );
};

const AddressAutocomplete = (props) => {
  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_PLACES_API_KEY} version="beta">
      <AddressAutocompleteInput {...props} />
    </APIProvider>
  );
};

export default AddressAutocomplete;
